// Copyright (C) 2021 Bystronic Laser AG

import { EChartsOption } from 'echarts';
import { FilterTimeAxisSpanEnum } from '../enums/FilterTimeAxisSpanEnum';
import { FilterTimeSpanEnum } from '../enums/FilterTimeSpanEnum';
import { ChartGenerator, ProcedureName } from './abstract/chartGenerator';
import { GeneratorParams } from './generatorParams';
import { BendTrendSeasonalProductivityTimelineData } from './chartsData';
import { formatPercentage } from '@/utils/number';
import { getTooltipTitle } from '@/utils/charts';
import { metricsService } from '@/services/metrics.service';
import moment from 'moment';
import { responseHandler } from '@/services/response.handler';

export default class BendTrendSeasonalProductivityTimelineGenerator extends ChartGenerator<
  BendTrendSeasonalProductivityTimelineData[]
> {
  constructor(procedure: ProcedureName, private readonly customerIdDh: number) {
    super(procedure);
  }

  override async getData(
    selectedDevices: string[],
    selectedShifts: number[],
    timeSpan: FilterTimeSpanEnum | [string, string],
    timeAxisSpan: FilterTimeAxisSpanEnum,
    params?: { [key: string]: any },
    customerNo?: string,
  ): Promise<BendTrendSeasonalProductivityTimelineData[] | null> {
    const dateRange = timeSpan as [string, string];
    return await metricsService
      .getBendingDevicesMetrics(
        this.procedure,
        {
          customerIdDh: this.customerIdDh,
          startDate: dateRange[0],
          endDate: dateRange[1],
          dateGrouping: timeAxisSpan,
          shiftIds: selectedShifts,
          deviceStringIds: selectedDevices,
        },
        this.controller,
      )
      .then((response) => responseHandler.handleDates(response));
  }

  override updateOptions(
    data: BendTrendSeasonalProductivityTimelineData[],
    parameters: GeneratorParams,
    prevOptions?: EChartsOption,
  ): EChartsOption {
    const years = [...new Set(data.map((x) => x.year))];
    const currentYear = moment().year();

    return {
      legend: {
        bottom: 0,
        selectedMode: years.length === 1 ? false : 'multiple',
      },
      xAxis: {
        type: 'time',
      },
      yAxis: {
        type: 'value',
        name: '%',
      },
      grid: {
        left: 30,
        right: 30,
        top: 30,
        bottom: 60,
      },
      tooltip: {
        trigger: 'axis',
        formatter: (params: any) => {
          const [xAxisValue] = params[0].value;
          const seriesInfo = params
            .map(
              (x: any) =>
                `<p>${x.marker}<span style="font-weight: bold">${
                  x.seriesName
                }</span>: ${formatPercentage(x.value[1])}</p>`,
            )
            .join('');
          return `
						<p>${getTooltipTitle(xAxisValue, parameters.timeAxisSpan!, false)}</p>
						${seriesInfo}
					`;
        },
        confine: false,
      },
      series: years.map((year) => ({
        name: `${year}`,
        type: 'line',
        data: data
          .filter((x) => x.year === year)
          .map((x) => [
            moment(x.bucket).year(currentYear).toDate(),
            x.productivityPercentage,
          ]),
      })),
    };
  }
}
